import React, { useEffect, useMemo, useState } from "react";
import { db, auth, storage } from "../../firebase/config";
import {
  collection,
  addDoc,
  doc,
  getDoc,
  updateDoc,
  deleteField,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FARM_STAND_AVAILABILITY_OPTIONS,
  getFreshFindStatusFromStandAvailability,
} from "../../constants/availability";
import {
  completeTrackedFlow,
  startTrackedFlow,
  trackUsageEvent,
} from "../Analytics/CampaignTrafficTracker";
import {
  abandonAppFlow,
  completeAppFlow,
  startAppFlow,
  trackAppEvent,
} from "../../utils/appAnalytics";
import { geocodeLocation } from "../../utils/geocode";
import {
  getMissingExactPublicCoordinatesMessage,
  parsePublicCoordinateInputs,
} from "../../utils/farmStandLocationForm";
import { STAND_LOCATION_MODES } from "../../utils/standLocation";
import SupportLink from "../Common/SupportLink";

const STAND_SAVE_SUPPORT_MESSAGE =
  "We couldn't save your stand. Please contact support and we'll help get it set up.";

const getSafeStandError = (error) => {
  const rawCode = error?.code || error?.name || "unknown";
  const rawMessage = error?.message || "";
  const code = String(rawCode).toLowerCase().replace(/[^a-z0-9_-]+/g, "-").slice(0, 80) || "unknown";

  if (rawMessage === "Stand / pickup spot not found.") {
    return { code: "stand-not-found", summary: "Stand not found" };
  }

  if (rawMessage === "You can only edit your own stand / pickup spot.") {
    return { code: "owner-mismatch", summary: "Owner mismatch" };
  }

  if (code.includes("permission-denied")) {
    return { code: "permission-denied", summary: "Permission denied" };
  }

  if (code.includes("storage")) {
    return { code, summary: "Image upload failed" };
  }

  return { code, summary: "Save failed" };
};

const buildSafeStandImageName = (file) => {
  const rawName = file?.name || "stand-pickup-photo";
  const extension = (rawName.split(".").pop() || "jpg")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "")
    .slice(0, 8) || "jpg";
  const baseName = rawName
    .replace(/\.[^/.]+$/, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80) || "stand-pickup-photo";

  return `${Date.now()}_${baseName}.${extension}`;
};

const looksLikePickupSpot = (...values) =>
  values
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .match(/\b(pickup|pick-up|porch|cooler|booth|market booth|drop[- ]?off)\b/);

const getStandKind = (...values) =>
  looksLikePickupSpot(...values) ? "pickup_spot" : "farm_stand";

const hasField = (record = {}, field) =>
  Object.prototype.hasOwnProperty.call(record || {}, field);

const AddFarmStand = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const user = auth.currentUser;
  const editId = searchParams.get("edit");
  const isEditing = Boolean(editId);

  const [standName, setStandName] = useState("");
  const [description, setDescription] = useState("");
  const [locationText, setLocationText] = useState("");
  const [city, setCity] = useState("");
  const [stateValue, setStateValue] = useState("");
  const [locationMode, setLocationMode] = useState(STAND_LOCATION_MODES.GENERAL);
  const [publicAddress, setPublicAddress] = useState("");
  const [publicLat, setPublicLat] = useState("");
  const [publicLng, setPublicLng] = useState("");
  const [hoursText, setHoursText] = useState("");
  const [standAvailabilityStatus, setStandAvailabilityStatus] = useState("open_now");
  const [openingDate, setOpeningDate] = useState("");
  const [seasonalNote, setSeasonalNote] = useState("");
  const [paymentMethods, setPaymentMethods] = useState({
    cash: true,
    cash_box: false,
    cash_app: false,
    venmo: false,
    paypal: false,
    other: false,
  });
  const [cashBoxNote, setCashBoxNote] = useState("");
  const [cashAppNote, setCashAppNote] = useState("");
  const [venmoNote, setVenmoNote] = useState("");
  const [paypalNote, setPaypalNote] = useState("");
  const [otherPaymentInstructions, setOtherPaymentInstructions] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadingStand, setLoadingStand] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [standImage, setStandImage] = useState(null);
  const [existingStandImage, setExistingStandImage] = useState("");
  const [paymentQrImage, setPaymentQrImage] = useState(null);
  const [existingPaymentQrImage, setExistingPaymentQrImage] = useState("");

  useEffect(() => {
    if (!user?.uid) return;
    startTrackedFlow("add_farm_stand", {
      feature: "farm_stand",
      action: isEditing ? "edit_stand_pickup" : "add_stand_pickup",
    });
    startAppFlow("farm_stand", "farm_stand_started", {
      feature: "farm_stand",
      action: isEditing ? "edit_stand_pickup" : "add_stand_pickup",
      abandonEventType: "farm_stand_abandoned",
    });

    return () => abandonAppFlow("farm_stand");
  }, [isEditing, user?.uid]);

  const standPreviewUrl = useMemo(() => {
    return standImage ? URL.createObjectURL(standImage) : null;
  }, [standImage]);

  const paymentQrPreviewUrl = useMemo(() => {
    return paymentQrImage ? URL.createObjectURL(paymentQrImage) : null;
  }, [paymentQrImage]);

  useEffect(() => {
    const loadFarmStandForEdit = async () => {
      if (!editId || !user?.uid) return;

      try {
        setLoadingStand(true);

        const standRef = doc(db, "farmStands", editId);
        const standSnap = await getDoc(standRef);

        if (!standSnap.exists()) {
          alert("Stand / pickup spot not found.");
          navigate(`/profile/${user.uid}`);
          return;
        }

        const stand = standSnap.data();

        if (stand.ownerId !== user.uid && stand.userId !== user.uid) {
          alert("You can only edit your own stand / pickup spot.");
          navigate(`/profile/${user.uid}`);
          return;
        }

        setStandName(stand.standName || "");
        setDescription(stand.description || "");
        setLocationText(stand.locationText || "");
        setCity(stand.city || "");
        setStateValue(stand.state || "");
        setLocationMode(
          stand.locationMode === STAND_LOCATION_MODES.EXACT ||
            stand.directionsEnabled === true
            ? STAND_LOCATION_MODES.EXACT
            : STAND_LOCATION_MODES.GENERAL
        );
        setPublicAddress(stand.publicAddress || "");
        setPublicLat(
          typeof stand.publicLat === "number" ? String(stand.publicLat) : ""
        );
        setPublicLng(
          typeof stand.publicLng === "number" ? String(stand.publicLng) : ""
        );
        setHoursText(stand.hoursText || "");
        setStandAvailabilityStatus(stand.standAvailabilityStatus || "open_now");
        setOpeningDate(stand.openingDate || "");
        setSeasonalNote(stand.seasonalNote || "");
        setExistingStandImage(stand.standImage || "");
        setCashBoxNote(stand.cashBoxNote || "");
        setCashAppNote(stand.cashAppNote || "");
        setVenmoNote(stand.venmoNote || "");
        setPaypalNote(stand.paypalNote || "");
        setOtherPaymentInstructions(stand.otherPaymentInstructions || "");
        setExistingPaymentQrImage(stand.paymentQrImage || "");

        setPaymentMethods({
          cash: stand.paymentMethods?.includes("cash") ?? true,
          cash_box: stand.paymentMethods?.includes("cash_box") ?? false,
          cash_app: stand.paymentMethods?.includes("cash_app") ?? false,
          venmo: stand.paymentMethods?.includes("venmo") ?? false,
          paypal: stand.paymentMethods?.includes("paypal") ?? false,
          other: stand.paymentMethods?.includes("other") ?? false,
        });
      } catch (error) {
        console.error("Error loading stand / pickup spot:", error);
        alert("Could not load stand / pickup spot for editing.");
      } finally {
        setLoadingStand(false);
      }
    };

    loadFarmStandForEdit();
  }, [editId, user?.uid, navigate]);

  const togglePayment = (method) => {
    setPaymentMethods((prev) => ({
      ...prev,
      [method]: !prev[method],
    }));
  };

  const selectedPaymentMethods = Object.entries(paymentMethods)
    .filter(([, checked]) => checked)
    .map(([method]) => method);

  const handleSaveStand = async () => {
    setSaveError("");

    if (!user) {
      setSaveError("Please log in first.");
      return;
    }

    if (!standName.trim()) {
      setSaveError("Please add a stand name.");
      return;
    }

    const broadLocationLabel =
      locationText.trim() ||
      [city.trim(), stateValue.trim().toUpperCase()].filter(Boolean).join(", ");
    const usesExactPublicLocation = locationMode === STAND_LOCATION_MODES.EXACT;

    if (!broadLocationLabel || !city.trim() || !stateValue.trim()) {
      setSaveError("Please add a general pickup area, city, and state.");
      return;
    }

    if (usesExactPublicLocation && !publicAddress.trim()) {
      setSaveError("Please add the exact public stand address for directions, or choose general area only.");
      return;
    }

    const {
      parsedPublicLat,
      parsedPublicLng,
      invalid: invalidPublicCoordinates,
    } = parsePublicCoordinateInputs(publicLat, publicLng);

    if (usesExactPublicLocation && invalidPublicCoordinates) {
      setSaveError("Please enter valid map pin coordinates, or leave them blank and use the public address.");
      return;
    }

    const standKind = getStandKind(standName, broadLocationLabel, publicAddress);
    const savedAsPickupSpot = standKind === "pickup_spot";
    const defaultFreshFindsStatus =
      getFreshFindStatusFromStandAvailability(standAvailabilityStatus);

    try {
      setSaving(true);

      let standImageUrl = "";

      if (standImage) {
        const imageRef = ref(
          storage,
          `farmStands/${user.uid}/${buildSafeStandImageName(standImage)}`
        );

        await uploadBytes(imageRef, standImage);
        standImageUrl = await getDownloadURL(imageRef);
      }

      let paymentQrImageUrl = "";

      if (paymentQrImage) {
        const qrImageRef = ref(
          storage,
          `farmStands/${user.uid}/${buildSafeStandImageName(paymentQrImage)}`
        );

        await uploadBytes(qrImageRef, paymentQrImage);
        paymentQrImageUrl = await getDownloadURL(qrImageRef);
      }

      let resolvedPublicLat = parsedPublicLat;
      let resolvedPublicLng = parsedPublicLng;

      if (
        usesExactPublicLocation &&
        (!Number.isFinite(resolvedPublicLat) || !Number.isFinite(resolvedPublicLng))
      ) {
        const geocodedPublicAddress = await geocodeLocation(publicAddress.trim());
        if (
          geocodedPublicAddress &&
          Number.isFinite(geocodedPublicAddress.lat) &&
          Number.isFinite(geocodedPublicAddress.lng)
        ) {
          resolvedPublicLat = geocodedPublicAddress.lat;
          resolvedPublicLng = geocodedPublicAddress.lng;
        }
      }

      const missingExactCoordinatesMessage = getMissingExactPublicCoordinatesMessage(
        usesExactPublicLocation,
        resolvedPublicLat,
        resolvedPublicLng
      );

      if (missingExactCoordinatesMessage) {
        setSaveError(missingExactCoordinatesMessage);
        return;
      }

      const standPayload = {
        standName: standName.trim(),
        description: description.trim(),
        locationText: broadLocationLabel,
        publicLocationLabel: broadLocationLabel,
        city: city.trim(),
        state: stateValue.trim().toUpperCase(),
        locationMode,
        directionsEnabled: usesExactPublicLocation,
        hoursText: hoursText.trim(),
        standAvailabilityStatus,
        serviceAvailabilityStatus: defaultFreshFindsStatus,
        openingDate: standAvailabilityStatus === "opening_soon" ? openingDate : "",
        seasonalNote:
          standAvailabilityStatus === "opening_soon" ||
          standAvailabilityStatus === "closed_for_season"
            ? seasonalNote.trim()
            : "",

        paymentMethods: selectedPaymentMethods,
        cashBoxNote: cashBoxNote.trim().slice(0, 160),
        cashAppNote: cashAppNote.trim().slice(0, 160),
        venmoNote: venmoNote.trim().slice(0, 160),
        paypalNote: paypalNote.trim().slice(0, 160),
        otherPaymentInstructions: otherPaymentInstructions.trim().slice(0, 220),
        contactPreference: "message",

        standKind,
        standType: standKind,
        status: "active",
        isActive: true,
        isArchived: false,
        isPublic: true,
        visibility: "public",
        updatedAt: serverTimestamp(),
      };

      if (usesExactPublicLocation) {
        standPayload.publicAddress = publicAddress.trim();
        if (Number.isFinite(resolvedPublicLat) && Number.isFinite(resolvedPublicLng)) {
          standPayload.publicLat = resolvedPublicLat;
          standPayload.publicLng = resolvedPublicLng;
        }
      }

      if (standImageUrl) {
        standPayload.standImage = standImageUrl;
      }

      if (paymentQrImageUrl) {
        standPayload.paymentQrImage = paymentQrImageUrl;
      }

      if (isEditing) {
        const standRef = doc(db, "farmStands", editId);
        const standSnap = await getDoc(standRef);

        if (!standSnap.exists()) {
          throw new Error("Stand / pickup spot not found.");
        }

        const existingStand = standSnap.data();

        if (
          existingStand.ownerId !== user.uid &&
          existingStand.userId !== user.uid
        ) {
          throw new Error("You can only edit your own stand / pickup spot.");
        }

        const editPayload = {
          ...standPayload,
        };

        if (!usesExactPublicLocation) {
          editPayload.publicAddress = deleteField();
          editPayload.publicLat = deleteField();
          editPayload.publicLng = deleteField();
        }

        if (!hasField(existingStand, "showInLocalFreshFinds")) {
          editPayload.showInLocalFreshFinds = true;
        }

        if (!existingStand.freshFindsStatus) {
          editPayload.freshFindsStatus = defaultFreshFindsStatus;
        }

        if (!Array.isArray(existingStand.freshTodayTags)) {
          editPayload.freshTodayTags = [];
        }

        if (!hasField(existingStand, "freshTodayNote")) {
          editPayload.freshTodayNote = "";
        }

        if (!existingStand.statusUpdatedAt) {
          editPayload.statusUpdatedAt = serverTimestamp();
        }

        await updateDoc(standRef, editPayload);
      } else {
        await addDoc(collection(db, "farmStands"), {
          ownerId: user.uid,
          userId: user.uid,
          ...standPayload,
          standImage: standImageUrl,
          showInLocalFreshFinds: true,
          freshFindsStatus: defaultFreshFindsStatus,
          freshTodayNote: "",
          freshTodayTags: [],
          statusUpdatedAt: serverTimestamp(),
          createdAt: serverTimestamp(),
        });
      }

      completeTrackedFlow("add_farm_stand");
      completeAppFlow("farm_stand", "farm_stand_completed", {
        feature: savedAsPickupSpot ? "pickup_spot" : "farm_stand",
        action: isEditing ? "update" : "create",
      });
      const standUsageEvent = isEditing
        ? savedAsPickupSpot
          ? "pickup_spot_updated"
          : "farm_stand_updated"
        : savedAsPickupSpot
          ? "pickup_spot_created"
          : "farm_stand_created";

      trackUsageEvent(standUsageEvent, {
        feature: savedAsPickupSpot ? "pickup_spot" : "farm_stand",
        action: isEditing ? "update" : "create",
        status: isEditing ? "updated" : "created",
      });

      alert(isEditing ? "Stand / pickup spot updated ✅" : "Stand / pickup spot saved ✅");
      navigate(`/profile/${user.uid}`);
    } catch (error) {
      const safeError = getSafeStandError(error);
      console.error("Stand / pickup spot save failed", {
        action: isEditing ? "update" : "create",
        errorCode: safeError.code,
        errorSummary: safeError.summary,
        hasStandImage: Boolean(standImage),
        hasPaymentQrImage: Boolean(paymentQrImage),
      });
      trackUsageEvent("stand_create_failed", {
        feature: savedAsPickupSpot ? "pickup_spot" : "farm_stand",
        action: isEditing ? "update" : "create",
        status: "error",
        errorCode: safeError.code,
        errorSummary: safeError.summary,
        allowRepeat: true,
      });
      trackAppEvent("stand_create_failed", {
        feature: savedAsPickupSpot ? "pickup_spot" : "farm_stand",
        action: isEditing ? "update" : "create",
        status: "error",
        errorCode: safeError.code,
        errorSummary: safeError.summary,
      });

      setSaveError(
        error.message === "You can only edit your own stand / pickup spot." ||
          error.message === "Stand / pickup spot not found."
          ? error.message
          : STAND_SAVE_SUPPORT_MESSAGE
      );
    } finally {
      setSaving(false);
    }
  };

  if (loadingStand) {
    return (
      <div className="add-product-page page-shell page-shell-narrow">
        <div className="card">Loading stand / pickup spot...</div>
      </div>
    );
  }

  return (
    <div className="add-product-page page-shell page-shell-narrow">
      <div className="add-product-hero">
        <div>
          <span className="edit-profile-kicker">🥕 Farm Stand / Pickup Spot</span>
          <h2>{isEditing ? "Edit Farm Stand / Pickup Spot" : "Add Farm Stand / Pickup Spot"}</h2>
          <p>
            Let local buyers know where they can pick up produce, eggs, meat,
            honey, handmade goods, or other farm products.
          </p>
        </div>
      </div>

      <div className="edit-section-card">
        <div className="edit-section-heading">
          <h3>Stand Details</h3>
          <p>
            Keep it simple. Buyers just need to know where, when, and how to pay.
          </p>
        </div>

        <div className="context-tip">
          <strong>Tip:</strong> This can be a roadside stand, porch pickup,
          cooler pickup, market booth, or local drop-off spot. Share only the
          pickup details you are comfortable making public.{" "}
          <SupportLink>Need help setting this up?</SupportLink>
        </div>

        {saveError && (
          <div className="form-error-panel" role="alert">
            <p>{saveError}</p>
            {saveError === STAND_SAVE_SUPPORT_MESSAGE && (
              <SupportLink>Contact support</SupportLink>
            )}
          </div>
        )}

        <div className="form-stack">
          <div>
            <label className="form-label">Stand / Pickup Spot Name</label>
            <input
              value={standName}
              onChange={(e) => setStandName(e.target.value)}
              placeholder="Example: Miller Family Farm Stand or Porch Pickup"
            />
          </div>

          <div>
            <label className="form-label">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              placeholder="Example: Fresh eggs, tomatoes, honey, and seasonal produce."
            />
          </div>

          <div>
            <label className="form-label">General public pickup area</label>
            <input
              value={locationText}
              onChange={(e) => setLocationText(e.target.value)}
              placeholder="Example: Bedford County, TN or Springfield, TN"
            />
            <p className="input-helper">
              This is the public area buyers see even when exact directions are private.
            </p>
          </div>

          <div className="product-form-row">
            <div>
              <label className="form-label">City</label>
              <input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Example: Springfield"
              />
            </div>

            <div>
              <label className="form-label">State</label>
              <input
                value={stateValue}
                onChange={(e) => setStateValue(e.target.value)}
                placeholder="Example: TN"
                maxLength={2}
              />
            </div>
          </div>

          <div className="form-panel">
            <label className="form-label">Location visibility</label>
            <div className="farm-stand-checks">
              <label>
                <input
                  type="radio"
                  name="standLocationMode"
                  value={STAND_LOCATION_MODES.GENERAL}
                  checked={locationMode === STAND_LOCATION_MODES.GENERAL}
                  onChange={() => setLocationMode(STAND_LOCATION_MODES.GENERAL)}
                />
                General area only
              </label>

              <label>
                <input
                  type="radio"
                  name="standLocationMode"
                  value={STAND_LOCATION_MODES.EXACT}
                  checked={locationMode === STAND_LOCATION_MODES.EXACT}
                  onChange={() => setLocationMode(STAND_LOCATION_MODES.EXACT)}
                />
                Exact public location with directions
              </label>
            </div>

            {locationMode === STAND_LOCATION_MODES.GENERAL ? (
              <p className="input-helper">
                Buyers will see your general area only. The stand page will ask them to message you for exact pickup details.
              </p>
            ) : (
              <div className="form-stack">
                <div className="context-tip">
                  <strong>Public directions warning:</strong> This address or map pin will be visible to buyers and usable for directions.
                </div>

                <label className="form-label">
                  Exact public stand address
                  <input
                    value={publicAddress}
                    onChange={(e) => setPublicAddress(e.target.value)}
                    placeholder="Example: 123 Farm Road, Springfield, TN"
                    maxLength={240}
                  />
                </label>

                <div className="product-form-row">
                  <label className="form-label">
                    Map pin latitude optional
                    <input
                      value={publicLat}
                      onChange={(e) => setPublicLat(e.target.value)}
                      placeholder="Example: 36.5099"
                      inputMode="decimal"
                    />
                  </label>

                  <label className="form-label">
                    Map pin longitude optional
                    <input
                      value={publicLng}
                      onChange={(e) => setPublicLng(e.target.value)}
                      placeholder="Example: -86.4507"
                      inputMode="decimal"
                    />
                  </label>
                </div>

                <p className="input-helper">
                  If you leave the map pin blank, MyFarmingPal will try to find coordinates from the public address.
                </p>
              </div>
            )}
          </div>

          <div>
            <label className="form-label">Hours / Availability</label>
            <input
              value={hoursText}
              onChange={(e) => setHoursText(e.target.value)}
              placeholder="Example: Saturdays 8 AM - 2 PM, or message before pickup"
            />
          </div>

          <div className="form-panel">
            <label className="form-label">
              Seasonal status
              <select
                value={standAvailabilityStatus}
                onChange={(e) => setStandAvailabilityStatus(e.target.value)}
              >
                {FARM_STAND_AVAILABILITY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            {standAvailabilityStatus === "opening_soon" && (
              <label className="form-label">
                Opening date optional
                <input
                  type="date"
                  value={openingDate}
                  onChange={(e) => setOpeningDate(e.target.value)}
                />
              </label>
            )}

            {(standAvailabilityStatus === "opening_soon" ||
              standAvailabilityStatus === "closed_for_season") && (
              <label className="form-label">
                Seasonal note optional
                <textarea
                  value={seasonalNote}
                  onChange={(e) => setSeasonalNote(e.target.value)}
                  rows={3}
                  placeholder="Example: Opening when strawberries are ready, or closed until spring."
                />
              </label>
            )}
          </div>

          <div>
            <label className="file-drop-card">
              <span>📸 {isEditing ? "Update Stand / Pickup Photo" : "Add Stand / Pickup Photo"}</span>
              <small>
                A photo helps buyers recognize your pickup spot when they arrive.
              </small>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setStandImage(e.target.files?.[0] || null)}
              />
            </label>

            {(standPreviewUrl || existingStandImage) && (
              <img
                src={standPreviewUrl || existingStandImage}
                className="preview-img"
                alt="Stand / pickup spot preview"
                style={{ marginTop: "12px" }}
              />
            )}
          </div>

          <div>
            <label className="form-label">Payment Methods</label>
            <p className="input-helper">
              These are seller-provided payment instructions only. MyFarmingPal does not process or verify outside payments.
            </p>

            <div className="farm-stand-checks">
              <label>
                <input
                  type="checkbox"
                  checked={paymentMethods.cash}
                  onChange={() => togglePayment("cash")}
                />
                Cash
              </label>

              <label>
                <input
                  type="checkbox"
                  checked={paymentMethods.cash_box}
                  onChange={() => togglePayment("cash_box")}
                />
                Cash box / honor box
              </label>

              <label>
                <input
                  type="checkbox"
                  checked={paymentMethods.cash_app}
                  onChange={() => togglePayment("cash_app")}
                />
                Cash App
              </label>

              <label>
                <input
                  type="checkbox"
                  checked={paymentMethods.venmo}
                  onChange={() => togglePayment("venmo")}
                />
                Venmo
              </label>

              <label>
                <input
                  type="checkbox"
                  checked={paymentMethods.paypal}
                  onChange={() => togglePayment("paypal")}
                />
                PayPal
              </label>

              <label>
                <input
                  type="checkbox"
                  checked={paymentMethods.other}
                  onChange={() => togglePayment("other")}
                />
                Other
              </label>
            </div>

            <div className="product-form-row">
              <label className="form-label">
                Cash box / honor box note
                <input
                  value={cashBoxNote}
                  onChange={(e) => setCashBoxNote(e.target.value)}
                  placeholder="Example: Cash box is on the porch."
                  maxLength={160}
                />
              </label>

              <label className="form-label">
                Cash App note
                <input
                  value={cashAppNote}
                  onChange={(e) => setCashAppNote(e.target.value)}
                  placeholder="Example: $YourCashtag"
                  maxLength={160}
                />
              </label>
            </div>

            <div className="product-form-row">
              <label className="form-label">
                Venmo note
                <input
                  value={venmoNote}
                  onChange={(e) => setVenmoNote(e.target.value)}
                  placeholder="Example: @YourVenmo"
                  maxLength={160}
                />
              </label>

              <label className="form-label">
                PayPal note
                <input
                  value={paypalNote}
                  onChange={(e) => setPaypalNote(e.target.value)}
                  placeholder="Example: PayPal.me/yourname"
                  maxLength={160}
                />
              </label>
            </div>

            <label className="form-label">
              Other payment instructions
              <textarea
                value={otherPaymentInstructions}
                onChange={(e) => setOtherPaymentInstructions(e.target.value)}
                rows={3}
                placeholder="Example: Message before paying digitally. Payment is handled directly with me."
                maxLength={220}
              />
            </label>

            <label className="file-drop-card">
              <span>Payment QR image optional</span>
              <small>Upload only a seller payment QR or simple payment image. Do not upload bank, card, or password information.</small>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setPaymentQrImage(e.target.files?.[0] || null)}
              />
            </label>

            {(paymentQrPreviewUrl || existingPaymentQrImage) && (
              <img
                src={paymentQrPreviewUrl || existingPaymentQrImage}
                className="preview-img"
                alt="Payment QR preview"
                style={{ marginTop: "12px" }}
              />
            )}
          </div>
        </div>
      </div>

      <div className="button-group add-product-actions">
        <button
          type="button"
          className="btn-soft"
          onClick={() => navigate(`/profile/${user?.uid}`)}
        >
          Cancel
        </button>

        <button
          type="button"
          className="btn-primary"
          onClick={handleSaveStand}
          disabled={saving}
        >
          {saving
            ? "Saving..."
            : isEditing
            ? "Update Stand / Pickup Spot"
            : "Save Stand / Pickup Spot"}
        </button>
      </div>
    </div>
  );
};

export default AddFarmStand;
